/**
 * The constraint choices behind the live viewfinder (CAPTURE_JOBS.md
 * section 6). Pure functions, so the rules are tested without a camera.
 *
 * The rear camera leads. `facingMode` is a bare value, which the browser
 * reads as a preference, never `exact`: `exact` throws
 * `OverconstrainedError` on a laptop that has only a front webcam, and the
 * preference is honoured by iOS Safari as well as by Chrome. The earlier
 * build asked iOS for `{ video: true }`, which opens the front camera on an
 * iPhone and points it at the operator.
 *
 * A flip steps through the `videoinput` devices by id rather than toggling
 * `facingMode`, because a phone with three rear lenses reports three
 * environment-facing devices and a toggle only ever reaches one of them.
 */

export type CameraConstraints = MediaStreamConstraints & { video: MediaTrackConstraints };

/** The first stream: the rear camera where one exists. */
export function rearCameraConstraints(): CameraConstraints {
  return { audio: false, video: { facingMode: 'environment' } };
}

/** A named device, reached by the flip control. */
export function deviceConstraints(deviceId: string): CameraConstraints {
  return { audio: false, video: { deviceId: { exact: deviceId } } };
}

/** The constraints for a stream: a chosen device, else the rear camera. */
export function cameraConstraints(deviceId?: string | null): CameraConstraints {
  return deviceId ? deviceConstraints(deviceId) : rearCameraConstraints();
}

/**
 * The cameras on this device. A browser that has not yet been granted
 * camera access reports devices with an empty id, which cannot be asked
 * for, so those are left out.
 */
export function videoInputs(
  devices: Pick<MediaDeviceInfo, 'kind' | 'deviceId' | 'label'>[],
): { deviceId: string; label: string }[] {
  return devices
    .filter((device) => device.kind === 'videoinput' && device.deviceId !== '')
    .map((device) => ({ deviceId: device.deviceId, label: device.label }));
}

/**
 * The camera the flip control moves to: the one after the current camera,
 * wrapping at the end. `null` when there is nothing to flip to. A current
 * id the list does not hold (a stream opened by `facingMode`, whose device
 * the browser did not name) moves to the first camera that is not it.
 */
export function nextDeviceId(inputs: { deviceId: string }[], currentId: string | undefined | null): string | null {
  if (inputs.length < 2) return null;
  const index = inputs.findIndex((input) => input.deviceId === currentId);
  if (index === -1) return inputs.find((input) => input.deviceId !== currentId)?.deviceId ?? null;
  return inputs[(index + 1) % inputs.length].deviceId;
}

export interface ZoomRange {
  min: number;
  max: number;
  step: number;
}

/** The track's own `zoom` capability, when it reports one. */
interface ZoomCapableTrack {
  getCapabilities?: () => MediaTrackCapabilities & { zoom?: { min?: number; max?: number; step?: number } };
}

/**
 * The zoom range the track reports, or `null` when it reports none. Chrome
 * on Android exposes `zoom` on the rear camera; iOS Safari and most desktop
 * webcams do not, and those get no zoom control rather than a dead one.
 */
export function readZoomRange(track: ZoomCapableTrack | null | undefined): ZoomRange | null {
  if (!track || typeof track.getCapabilities !== 'function') return null;
  let zoom: { min?: number; max?: number; step?: number } | undefined;
  try {
    zoom = track.getCapabilities().zoom;
  } catch {
    return null;
  }
  if (!zoom || typeof zoom.min !== 'number' || typeof zoom.max !== 'number') return null;
  if (!(zoom.max > zoom.min)) return null;
  const step = typeof zoom.step === 'number' && zoom.step > 0 ? zoom.step : 0.1;
  return { min: zoom.min, max: zoom.max, step };
}

export function clampZoom(range: ZoomRange, value: number): number {
  return Math.min(range.max, Math.max(range.min, value));
}

/** What `applyConstraints` is handed to set the zoom. `advanced`, because a
 *  device that refuses the value then ignores it rather than failing. */
export function zoomConstraints(value: number): MediaTrackConstraints {
  return { advanced: [{ zoom: value } as MediaTrackConstraintSet] };
}

/**
 * Whether this browser can open a camera stream at all. The only condition
 * the reduced-capability notice reports: a missing `BarcodeDetector` has a
 * JavaScript decoder behind it (`lib/barcode.ts`), and a missing zoom has
 * the native camera behind "Use phone camera".
 */
export function isCameraAvailable(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';
}
